(() => {
  const ROOT_CLASS = 'ob-search-popover';
  const SEARCH_TRIGGER_SELECTOR = [
    '.menu-list__link:has(.ob-header-icon--search)',
    'a.ob-mobile-actions__icon[aria-label="Search"]',
    'a.over-bros-inner-icon--search'
  ].join(',');
  const DEBOUNCE_MS = 220;
  const DESKTOP_QUERY = '(min-width: 990px) and (hover: hover) and (pointer: fine)';
  const TOUCH_LANDSCAPE_QUERY = '(min-width: 990px) and (max-width: 1366px) and (hover: none) and (pointer: coarse)';

  if (window.__overBrosSearchPopoverInitialized) return;

  const rootPath = window.Shopify?.routes?.root || '/';
  const searchPath = `${rootPath}search`;
  const suggestPath = `${rootPath}search/suggest.json`;
  const currency = window.Shopify?.currency?.active || 'USD';
  const locale = document.documentElement.lang || undefined;

  let activeTrigger = null;
  let debounceTimer = null;
  let requestController = null;
  let positionFrame = null;
  let ready = false;

  const make = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text != null) element.textContent = text;
    return element;
  };

  const popover = make('div', ROOT_CLASS);
  popover.hidden = true;
  popover.setAttribute('role', 'search');
  popover.setAttribute('aria-label', 'Search products and pages');

  const form = make('form', `${ROOT_CLASS}__form`);
  form.action = searchPath;
  form.method = 'get';

  const inputRow = make('div', `${ROOT_CLASS}__input-row`);
  const icon = make('span', `${ROOT_CLASS}__icon`);
  icon.setAttribute('aria-hidden', 'true');
  icon.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" stroke-width="1.5"></circle><path d="m16 16 4 4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></path></svg>';

  const input = make('input', `${ROOT_CLASS}__input`);
  input.type = 'search';
  input.name = 'q';
  input.placeholder = 'Search';
  input.autocomplete = 'off';
  input.setAttribute('aria-label', 'Search');
  input.setAttribute('aria-controls', 'ob-search-popover-results');
  input.setAttribute('aria-expanded', 'false');

  const closeButton = make('button', `${ROOT_CLASS}__close`);
  closeButton.type = 'button';
  closeButton.setAttribute('aria-label', 'Close search');
  closeButton.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></path></svg>';

  inputRow.append(icon, input, closeButton);

  const results = make('div', `${ROOT_CLASS}__results`);
  results.id = 'ob-search-popover-results';
  results.hidden = true;
  results.setAttribute('aria-live', 'polite');
  results.setAttribute('aria-busy', 'false');

  const viewAll = make('a', `${ROOT_CLASS}__view-all`, 'View all results');
  viewAll.hidden = true;

  form.append(inputRow, results, viewAll);
  popover.append(form);

  const getMode = () => {
    if (window.matchMedia(DESKTOP_QUERY).matches) return 'desktop';
    if (window.matchMedia(TOUCH_LANDSCAPE_QUERY).matches) return 'touch-landscape';
    if (window.innerWidth <= 749) return 'mobile';
    if (window.innerWidth <= 989) return 'tablet-portrait';
    return 'touch-landscape';
  };

  const getHeaderBottom = (trigger) => {
    const innerHeader = trigger?.closest('.over-bros-inner-header');
    const headerComponent = trigger?.closest('header-component');
    const header = innerHeader || headerComponent;
    return header?.getBoundingClientRect().bottom ?? trigger?.getBoundingClientRect().bottom ?? 0;
  };

  const positionPopover = () => {
    if (popover.hidden || !activeTrigger) return;

    const mode = getMode();
    const triggerRect = activeTrigger.getBoundingClientRect();
    const headerBottom = getHeaderBottom(activeTrigger);
    const top = Math.max(8, Math.min(window.innerHeight - 96, Math.round(headerBottom + 8)));

    popover.dataset.mode = mode;
    popover.style.setProperty('--ob-search-top', `${top}px`);

    if (mode === 'desktop') {
      const width = Math.min(420, window.innerWidth - 24);
      const preferredLeft = triggerRect.right - width;
      const left = Math.max(12, Math.min(preferredLeft, window.innerWidth - width - 12));
      popover.style.setProperty('--ob-search-left', `${Math.round(left)}px`);
    } else {
      popover.style.removeProperty('--ob-search-left');
    }
  };

  const schedulePosition = () => {
    if (positionFrame != null) return;
    positionFrame = window.requestAnimationFrame(() => {
      positionFrame = null;
      positionPopover();
    });
  };

  const searchUrlFor = (query) => {
    const url = new URL(searchPath, window.location.origin);
    if (query) url.searchParams.set('q', query);
    return url.pathname + url.search;
  };

  const formatPrice = (rawPrice) => {
    let candidate = rawPrice;
    if (candidate && typeof candidate === 'object') {
      candidate = candidate.amount ?? candidate.value ?? candidate.min ?? '';
    }

    const numeric = typeof candidate === 'number' ? candidate : Number(String(candidate).trim());
    if (Number.isFinite(numeric)) {
      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(numeric);
      } catch (_) {
        return String(candidate);
      }
    }

    return candidate == null ? '' : String(candidate);
  };

  const imageUrlFor = (product) => {
    const featured = product?.featured_image;
    if (featured && typeof featured === 'object' && featured.url) return featured.url;
    if (typeof featured === 'string') return featured;
    if (typeof product?.image === 'string') return product.image;
    if (product?.image?.url) return product.image.url;
    return '';
  };

  const createSection = (title) => {
    const section = make('section', `${ROOT_CLASS}__section`);
    const heading = make('h3', `${ROOT_CLASS}__heading`, title);
    section.append(heading);
    return section;
  };

  const createProductResult = (product) => {
    const link = make('a', `${ROOT_CLASS}__result ${ROOT_CLASS}__result--product`);
    link.href = product.url;

    const imageUrl = imageUrlFor(product);
    if (imageUrl) {
      const image = make('img', `${ROOT_CLASS}__image`);
      image.src = imageUrl;
      image.alt = product.title || '';
      image.loading = 'lazy';
      link.append(image);
    } else {
      const imageSpace = make('span', `${ROOT_CLASS}__image ${ROOT_CLASS}__image--empty`);
      imageSpace.setAttribute('aria-hidden', 'true');
      link.append(imageSpace);
    }

    const content = make('span', `${ROOT_CLASS}__content`);
    content.append(make('span', `${ROOT_CLASS}__title`, product.title || ''));

    const price = formatPrice(product.price);
    if (price) content.append(make('span', `${ROOT_CLASS}__price`, price));

    link.append(content);
    return link;
  };

  const createTextResult = (item, type) => {
    const link = make('a', `${ROOT_CLASS}__result ${ROOT_CLASS}__result--text`);
    link.href = item.url || searchUrlFor(item.text || item.title || '');
    const label = item.text || item.title || '';
    link.append(make('span', `${ROOT_CLASS}__title`, label));
    if (type) link.append(make('span', `${ROOT_CLASS}__type`, type));
    return link;
  };

  const renderResults = (payload, query) => {
    const resources = payload?.resources?.results || {};
    const products = Array.isArray(resources.products) ? resources.products : [];
    const queries = Array.isArray(resources.queries) ? resources.queries : [];
    const collections = Array.isArray(resources.collections) ? resources.collections : [];
    const pages = Array.isArray(resources.pages) ? resources.pages : [];

    results.replaceChildren();

    if (products.length) {
      const section = createSection('Products');
      const list = make('div', `${ROOT_CLASS}__list`);
      products.forEach((product) => list.append(createProductResult(product)));
      section.append(list);
      results.append(section);
    }

    const secondary = [...queries.map((item) => [item, 'Suggestion']), ...collections.map((item) => [item, 'Collection']), ...pages.map((item) => [item, 'Page'])];
    if (secondary.length) {
      const section = createSection('Explore');
      const list = make('div', `${ROOT_CLASS}__list ${ROOT_CLASS}__list--secondary`);
      secondary.slice(0, 6).forEach(([item, type]) => list.append(createTextResult(item, type)));
      section.append(list);
      results.append(section);
    }

    if (!products.length && !secondary.length) {
      results.append(make('p', `${ROOT_CLASS}__empty`, `No results for “${query}”.`));
    }

    results.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    viewAll.href = searchUrlFor(query);
    viewAll.hidden = false;
    schedulePosition();
  };

  const clearResults = () => {
    results.replaceChildren();
    results.hidden = true;
    results.setAttribute('aria-busy', 'false');
    input.setAttribute('aria-expanded', 'false');
    viewAll.hidden = true;
    viewAll.removeAttribute('href');
  };

  const requestPredictions = async (query) => {
    requestController?.abort();
    requestController = new AbortController();

    const url = new URL(suggestPath, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,collection,page,query');
    url.searchParams.set('resources[limit_scope]', 'each');

    results.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
        signal: requestController.signal,
      });
      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
      const payload = await response.json();
      if (input.value.trim() !== query) return;
      renderResults(payload, query);
    } catch (error) {
      if (error?.name !== 'AbortError') clearResults();
    } finally {
      results.setAttribute('aria-busy', 'false');
    }
  };

  const closePopover = ({ restoreFocus = false } = {}) => {
    if (popover.hidden) return;
    popover.hidden = true;
    requestController?.abort();
    requestController = null;
    window.clearTimeout(debounceTimer);
    debounceTimer = null;
    input.value = '';
    clearResults();
    const trigger = activeTrigger;
    activeTrigger = null;
    popover.removeAttribute('data-mode');
    popover.style.removeProperty('--ob-search-top');
    popover.style.removeProperty('--ob-search-left');
    if (restoreFocus) trigger?.focus?.();
  };

  const openPopover = (trigger) => {
    activeTrigger = trigger;
    popover.hidden = false;
    positionPopover();
    window.requestAnimationFrame(() => input.focus({ preventScroll: true }));
  };

  input.addEventListener('input', () => {
    const query = input.value.trim();
    window.clearTimeout(debounceTimer);
    requestController?.abort();

    if (!query) {
      clearResults();
      return;
    }

    debounceTimer = window.setTimeout(() => requestPredictions(query), DEBOUNCE_MS);
  });

  form.addEventListener('submit', (event) => {
    const query = input.value.trim();
    if (!query) {
      event.preventDefault();
      input.focus();
      return;
    }
    form.action = searchPath;
  });

  closeButton.addEventListener('click', () => closePopover({ restoreFocus: true }));

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !popover.hidden) {
      event.preventDefault();
      closePopover({ restoreFocus: true });
    }
  });

  document.addEventListener('pointerdown', (event) => {
    if (popover.hidden) return;
    if (popover.contains(event.target)) return;
    const trigger = event.target instanceof Element ? event.target.closest(SEARCH_TRIGGER_SELECTOR) : null;
    if (trigger) return;
    closePopover();
  });

  window.addEventListener('resize', schedulePosition);
  window.addEventListener('scroll', schedulePosition, { passive: true });
  window.addEventListener('orientationchange', schedulePosition);

  try {
    const triggers = document.querySelectorAll(SEARCH_TRIGGER_SELECTOR);
    if (!triggers.length) return;

    document.body.append(popover);

    if (!popover.isConnected || !input || !results || !closeButton || !form) {
      popover.remove();
      return;
    }

    ready = true;
    window.__overBrosSearchPopoverInitialized = true;

    document.addEventListener('click', (event) => {
      if (!ready) return;
      const trigger = event.target instanceof Element ? event.target.closest(SEARCH_TRIGGER_SELECTOR) : null;
      if (!(trigger instanceof HTMLAnchorElement)) return;

      event.preventDefault();

      if (!popover.hidden) {
        activeTrigger = trigger;
        positionPopover();
        input.focus({ preventScroll: true });
        return;
      }

      openPopover(trigger);
    });
  } catch (_) {
    ready = false;
    popover.remove();
  }
})();
